import { router, useLocalSearchParams } from 'expo-router';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useState } from 'react';

import { useValidateOtpV2Mutation } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';

import { cognitoSignIn } from '../cognito';
import { AuthV2ChallengeType, IdentifyAuthJwt } from '../types';

import { useAuthContext, useCRMEventsContext, useToast } from '.';

export const useValidateOtp = ({ onIdentifyChallenge }) => {
  const [identifyAuthJwts, setIdentifyAuthJwts] = useState<IdentifyAuthJwt[]>([]);
  const [validatingOtp, setValidatingOtp] = useState<boolean>(false);
  const [otpValidationError, setOtpValidationError] = useState<boolean>(false);
  const isDesktop = useIsDesktopBp();
  const { loginJwt } = useLocalSearchParams<{ loginJwt?: string }>();
  //When we are fully on auth V2 we should remove this state and just handle the authenticated state ourselves.
  const { refreshCurrentUserWithNewSession, originLocation } = useAuthContext();
  const { logLoginEvent, logValidateAttempt } = useCRMEventsContext();
  const onValidateOtp = useCallback((data, _clientOptions) => {
    router.setParams({ loginJwt: data.validateOtpV2.loginJwt });
    return data;
  }, []);
  const { show } = useToast();
  const onError = useCallback(
    err => {
      if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
        //Take the highest level error code
        const code = err.graphQLErrors[0].extensions.code;
        switch (code) {
          case 'INVALID_OTP':
            setOtpValidationError(true);
            break;

          case 'NO_VALID_CODES':
            router.dismissTo('/v2/signin');
            show({
              text: "You've exceeded the limit of attempts. Please request a new code and try again.",
              variant: 'informative',
            });
            break;
          case 'TOO_MANY_REQUESTS':
            show({
              text: 'You have exceeded the limit of attempts. Please try again later.',
              variant: 'informative',
            });
            break;
          default:
            router.dismissTo('/v2/signin');
            show({ text: 'Oops, something went wrong. Please try again', variant: 'informative' });
        }
      } else {
        router.dismissTo('/v2/signin');
        show({ text: 'Oops, something went wrong. Please try again', variant: 'informative' });
      }
    },
    [show]
  );
  const [internalValidateOtp] = useValidateOtpV2Mutation({
    onCompleted: onValidateOtp,
    onError: onError,
  });
  const validateOtp = useCallback(
    async (otpInput: string) => {
      try {
        setValidatingOtp(true);
        if (loginJwt) {
          const response = await internalValidateOtp({
            variables: { input: { otpInput, loginJwt } },
          });
          const validateResult = response.data?.validateOtpV2;
          logValidateAttempt({
            successful: !!validateResult,
            challengeType: validateResult?.challengeType || AuthV2ChallengeType.SIGN_IN_CHALLENGE,
            numAccounts:
              validateResult?.challenge?.authJwts?.length ??
              (validateResult?.challengeType === AuthV2ChallengeType.SIGN_UP_CHALLENGE ? 0 : 1),
          });
          if (validateResult) {
            switch (validateResult.challengeType) {
              case AuthV2ChallengeType.SIGN_IN_CHALLENGE: {
                const { challengeCode, cognitoId, sessionId } = validateResult.challenge;
                //Authenticate with Cognito.
                const session = await cognitoSignIn(cognitoId, sessionId, challengeCode);
                //Validate the user is authenticated here
                if (session) {
                  //Update Local storage session info and trigger downstream listeners
                  await refreshCurrentUserWithNewSession();
                  //Push event to Amplitude
                  logLoginEvent({ method: 'signIn' });
                  if (originLocation === '/cart') {
                    // only added because of the flag per user; needs to wait for flag to update
                    await new Promise(resolve => setTimeout(resolve, 300));

                    router.dismissTo({ pathname: '/cart', params: { loginJwt: undefined } });
                    break;
                  }
                  if (originLocation === 'PYPESTREAM' || originLocation === '/support') {
                    // authContext needs time to update the cognitoId, to avoid a race condition, give a small wait time before navigating
                    await new Promise(resolve => setTimeout(resolve, 300));
                    router.dismissTo({ pathname: '/support', params: { loginJwt: undefined } });
                  } else {
                    //Route to home page & clear login state
                    router.dismissTo({ pathname: '/', params: { loginJwt: undefined } });
                  }
                }
                break;
              }
              case AuthV2ChallengeType.SIGN_UP_CHALLENGE: {
                router.replace({
                  pathname: '/v2/signin/signup',
                  params: { loginJwt: validateResult.loginJwt },
                });
                break;
              }
              case AuthV2ChallengeType.IDENTIFY_CHALLENGE: {
                //Render select account popup
                const { authJwts } = validateResult.challenge;
                setIdentifyAuthJwts(
                  authJwts?.map(jwt => {
                    return {
                      ...jwtDecode<IdentifyAuthJwt>(jwt),
                      jwt,
                    };
                  }) ?? []
                );
                onIdentifyChallenge();
                break;
              }
            }
          }
        }
      } catch (e) {
        //Pretty unlikely case, but if the signin fails hard we will move the user back to sign in and display a toast message.
        show({
          text: 'Oops, something went wrong. Please try again',
          variant: 'informative',
          duration: 5000,
        });
        router.dismissAll();
      }
      setValidatingOtp(false);
    },
    [
      internalValidateOtp,
      isDesktop,
      logLoginEvent,
      logValidateAttempt,
      loginJwt,
      onIdentifyChallenge,
      originLocation,
      refreshCurrentUserWithNewSession,
      show,
    ]
  );
  return {
    validatingOtp,
    otpValidationError,
    setOtpValidationError,
    validateOtp,
    identifyAuthJwts,
  };
};
